import { FC, useMemo } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { formatDateToFullDate } from "@simplyk/common";
import { useForm } from "react-hook-form";

import {
  CancellationReason,
  DONATION_CANCELLATION_REASONS,
  MEMBERSHIP_CANCELLATION_REASONS,
} from "./helpers/constants";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { DashboardRadioGroup } from "@/dashboard-design-system/RadioGroup";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { RenewCancel } from "@/icons/outlined";

interface CancelSubscriptionFormData {
  cancellationReason: CancellationReason;
  cancellationFeedback: string;
}

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { cancellationReason: CancellationReason; cancellationFeedback: string }) => Promise<void>;
  isLoading: boolean;
  isMembership: boolean;
  currentPeriodEnd?: Date | string | null;
}

export const CancelSubscriptionDialog: FC<CancelSubscriptionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  isMembership,
  currentPeriodEnd,
}) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();

  const { control, handleSubmit, formState, reset } = useForm<CancelSubscriptionFormData>({
    defaultValues: {
      cancellationReason: "" as unknown as CancellationReason,
      cancellationFeedback: "",
    },
  });

  const reasonChoicesMap = useMemo(() => {
    if (isMembership) {
      return MEMBERSHIP_CANCELLATION_REASONS.reduce(
        (acc, reason) => ({
          ...acc,
          [reason]: t("dashboard", `donorTransactionDetailDrawer.cancelDialog.membershipReason.${reason}`),
        }),
        {} as Record<string, string>
      );
    }
    return DONATION_CANCELLATION_REASONS.reduce(
      (acc, reason) => ({
        ...acc,
        [reason]: t("dashboard", `donorTransactionDetailDrawer.cancelDialog.donationReason.${reason}`),
      }),
      {} as Record<string, string>
    );
  }, [t, isMembership]);

  const formattedPeriodEnd = useMemo(() => {
    if (!currentPeriodEnd) {
      return null;
    }
    return formatDateToFullDate(new Date(currentPeriodEnd), isoLocale);
  }, [currentPeriodEnd, isoLocale]);

  const title = isMembership
    ? t("dashboard", "donorTransactionDetailDrawer.cancelDialog.membershipTitle")
    : t("dashboard", "donorTransactionDetailDrawer.cancelDialog.donationTitle");

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    await onConfirm(data);
    handleClose();
  });

  return (
    <DashboardDialog open={open} onClose={handleClose} maxWidth="xs" vibe="danger">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<RenewCancel />} title={title} />,
          content: (
            <DashboardDialogContent>
              <Stack spacing={2}>
                <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                  {t("dashboard", "donorTransactionDetailDrawer.cancelDialog.body")}
                </DashboardTypography>

                <Box
                  sx={{
                    '& [data-test="base-radio-group-choice"] + [data-test="base-radio-group-choice"]': {
                      marginTop: "8px !important",
                    },
                  }}
                >
                  <DashboardRadioGroup
                    control={control}
                    name="cancellationReason"
                    values={reasonChoicesMap}
                    rules={[{ required: { value: true, message: t("dashboard", "common.isRequired") as string } }]}
                    error={Boolean(formState.errors.cancellationReason)}
                    errorMessage={formState.errors.cancellationReason?.message}
                  />
                </Box>

                <DashboardTextField
                  control={control}
                  name="cancellationFeedback"
                  label={t("dashboard", "donorTransactionDetailDrawer.cancelDialog.feedbackLabel")}
                  placeholder={t("dashboard", "donorTransactionDetailDrawer.cancelDialog.feedbackPlaceholder")}
                  multiline
                  rows={3}
                  maxRows={5}
                />

                {isMembership && formattedPeriodEnd && (
                  <DashboardInfobox vibe="brand" size="small">
                    {t("dashboard", "donorTransactionDetailDrawer.cancelDialog.membershipActiveUntil", {
                      date: formattedPeriodEnd,
                    })}
                  </DashboardInfobox>
                )}
              </Stack>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={onSubmit} isLoading={isLoading}>
                {t("dashboard", "donorTransactionDetailDrawer.cancelDialog.confirm")}
              </DashboardButton>
              <DashboardButton onClick={handleClose}>
                {isMembership
                  ? t("dashboard", "donorTransactionDetailDrawer.cancelDialog.keepMembership")
                  : t("dashboard", "donorTransactionDetailDrawer.cancelDialog.keepDonation")}
              </DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
